import { useCallback, useEffect, useState } from 'react'

import { ActionIcon, Tooltip } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import dayjs from 'dayjs'

import { useMutation, useQuery } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import DnTable from 'src/components/Core/DnTable'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_CUSTOM_STEP,
  GET_CUSTOM_STEPS,
} from 'src/graphql/customSteps.graphql'
import IconCopy from 'src/icons/IconCopy'

import ActionsCell from './components/ActionsCell'
import CustomStepModal from './components/CustomStepModal'

const LIMIT = 20

const CustomStepsTab = () => {
  const [selectedStep, setSelectedStep] = useState(null)
  const [stepToDelete, setStepToDelete] = useState(null)
  const [editing, setEditing] = useState(false)
  const [modalOpened, modalHandlers] = useDisclosure(false)
  const [deleteModalOpened, deleteModalHandlers] = useDisclosure(false)

  const onError = useCallback(() => {
    toast('Something went wrong, please try again.', 'error')
  }, [])

  const handleCopyId = useCallback((e, id: string) => {
    e.stopPropagation()
    navigator?.clipboard?.writeText?.(id)
    toast('Custom step ID copied to clipboard', 'success')
  }, [])

  const { data, loading, refetch } = useQuery(GET_CUSTOM_STEPS, {
    variables: { pageInfo: { limit: LIMIT, offset: 0 } },
    onError,
  })

  const handleLoadPage = useCallback(
    ({ offset, limit }: { offset: number; limit: number }) => {
      refetch({ pageInfo: { limit, offset } })
    },
    [refetch]
  )

  const [deleteCustomStep] = useMutation(DELETE_CUSTOM_STEP, {
    refetchQueries: [GET_CUSTOM_STEPS],
    onCompleted: () => {
      toast('Custom step archived', 'success')
      // Reset to the first page so archiving the last row on a later page
      // doesn't strand DnTable on an empty, out-of-range page.
      handleLoadPage({ offset: 0, limit: LIMIT })
    },
    onError,
  })

  const onCreate = () => {
    setSelectedStep(undefined)
    setEditing(false)
    modalHandlers.open()
  }

  const onEdit = (step) => {
    setSelectedStep(step)
    setEditing(true)
    modalHandlers.open()
  }

  const handleDelete = (step) => {
    setStepToDelete(step)
    deleteModalHandlers.open()
  }

  const onDelete = async () => {
    if (!stepToDelete) return
    // Awaited so DeleteModal shows its spinner and closes only after the
    // mutation resolves. The mutation's onError keeps the promise from
    // rejecting, so DeleteModal's own close() still runs.
    await deleteCustomStep({ variables: { id: stepToDelete.customStepId } })
  }

  // Clear selection once the create/edit modal closes
  useEffect(() => {
    if (!modalOpened) setSelectedStep(null)
  }, [modalOpened])

  const steps = data?.customSteps?.items ?? []
  const totalCount = data?.customSteps?.totalCount || 0

  const columns = [
    {
      key: 'customStepId',
      label: 'ID',
      width: 4,
      render: (step) => (
        <div className="flex items-center gap-2">
          <Tooltip label={step.customStepId} withArrow>
            <ActionIcon
              variant="subtle"
              size={18}
              color="gray"
              onClick={(e) => handleCopyId(e, step.customStepId)}
            >
              <IconCopy className="h-3.5 w-3.5 fill-none stroke-doubleNickel-gray-500" />
            </ActionIcon>
          </Tooltip>
        </div>
      ),
    },
    {
      key: 'label',
      label: 'Step',
      width: 35,
      render: (step) => step.label,
    },
    {
      key: 'usage',
      label: 'Used in',
      width: 15,
      render: (step) => {
        const count = step.templateSteps?.length ?? 0
        return `${count} template${count === 1 ? '' : 's'}`
      },
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (step) => dayjs(step.createdAt).format('MM-DD-YYYY'),
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      render: (step) => dayjs(step.updatedAt).format('MM-DD-YYYY'),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: 10,
      render: (step) => (
        <ActionsCell
          key={`actions-${step.customStepId}`}
          customStep={step}
          onEdit={onEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-row justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Custom Steps
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {loading ? 'loading...' : `${totalCount} custom steps`}
          </div>
        </div>
        <DNButton variant="primary" onClick={onCreate}>
          Create Custom Step
        </DNButton>
      </div>
      <div className="min-h-0 flex-1">
        <DnTable
          columns={columns}
          data={steps}
          enableColumnResize={false}
          totalCount={totalCount}
          onLoadPage={handleLoadPage}
          loading={loading}
          onRowClick={(idx) => onEdit(steps[idx])}
          emptyStateMessage="No custom steps yet. Create one to get started."
        />
      </div>
      <CustomStepModal
        customStep={selectedStep}
        edit={editing}
        isOpen={modalOpened}
        onClose={modalHandlers.close}
        refetchQueries={[GET_CUSTOM_STEPS]}
      />
      <DeleteModal
        title="Delete Custom Step"
        description={
          stepToDelete ? (
            <div className="space-y-2">
              <p>Are you sure you want to delete this custom step?</p>
              <div className="rounded-md bg-doubleNickel-gray-50 p-3">
                <p className="break-all text-sm text-doubleNickel-gray-900">
                  {stepToDelete.label}
                </p>
              </div>
            </div>
          ) : (
            'This action cannot be undone.'
          )
        }
        opened={deleteModalOpened}
        close={deleteModalHandlers.close}
        onDelete={onDelete}
      />
    </div>
  )
}

export default CustomStepsTab
